import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import PageHeader from '../components/common/PageHeader';
import { useNotification } from '../hooks/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import {
  importExportService,
  ExportableEntity,
  ImportResult,
} from '../services/importExportService';

// Reihenfolge-Stufen des Backends (ENTITY_REGISTRY). Entitäten einer Stufe
// hängen nur von Entitäten kleinerer Stufen ab.
const IMPORT_CATEGORY_LABELS: Record<number, string> = {
  1: 'Stammdaten',
  2: 'Basisdaten',
  3: 'Abhängige Daten',
  4: 'Detaildaten',
  5: 'Weiterführende Detaildaten',
  99: 'Unbekannte Entitäten',
};
const UNKNOWN_ENTITY_ORDER = 99;

const DatenImportExportPage: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const canExport = hasPermission(P.DATA_EXPORT);
  const canImport = hasPermission(P.DATA_IMPORT);

  const [exportableEntities, setExportableEntities] = useState<ExportableEntity[]>([]);
  const [selectedExportEntities, setSelectedExportEntities] = useState<Set<string>>(new Set());
  const [exportIncludeDependencies, setExportIncludeDependencies] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importFileEntities, setImportFileEntities] = useState<{ key: string; count: number }[]>([]);
  const [selectedImportEntities, setSelectedImportEntities] = useState<Set<string>>(new Set());
  const [importStrategy, setImportStrategy] = useState<'skip_existing' | 'overwrite' | 'fail_on_conflict'>('skip_existing');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  const loadExportableEntities = async () => {
    if (!canExport) return;
    try {
      const data = await importExportService.getExportableEntities();
      setExportableEntities(data);
    } catch {
      // Error displayed by axios interceptor
    }
  };

  useEffect(() => {
    if (canExport) loadExportableEntities();
  }, [canExport]);

  const handleExport = async () => {
    const entities = Array.from(selectedExportEntities);
    if (entities.length === 0) {
      showError('Bitte mindestens eine Entität auswählen');
      return;
    }
    try {
      setExportLoading(true);
      const blob = await importExportService.exportData(entities, undefined, exportIncludeDependencies);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = entities.includes('files') ? 'zip' : 'json';
      a.download = `stark-export-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccess('Export erfolgreich');
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportFileChange = async (f: File | null) => {
    setImportFile(f);
    setImportResult(null);
    setImportFileEntities([]);
    setSelectedImportEntities(new Set());
    if (!f) return;
    const parseJsonData = (data: { entities?: Record<string, unknown> }) => {
      const entities = data?.entities;
      if (!entities || typeof entities !== 'object') {
        showError('Keine gültige Export-Datei (entities fehlt)');
        setImportFile(null);
        return;
      }
      const list = Object.keys(entities).map((key) => ({
        key,
        count: Array.isArray(entities[key]) ? (entities[key] as unknown[]).length : 0,
      }));
      setImportFileEntities(list);
      setSelectedImportEntities(new Set(list.map((e) => e.key)));
    };
    try {
      if (f.name.toLowerCase().endsWith('.zip')) {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(f);
        const dataJson = zip.file('data.json');
        if (!dataJson) {
          showError('ZIP-Archiv enthält keine data.json');
          setImportFile(null);
          return;
        }
        const text = await dataJson.async('string');
        const data = JSON.parse(text);
        parseJsonData(data);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result as string;
            const data = JSON.parse(text);
            parseJsonData(data);
          } catch {
            showError('Datei konnte nicht gelesen werden (ungültiges JSON)');
            setImportFile(null);
          }
        };
        reader.readAsText(f, 'UTF-8');
      }
    } catch {
      showError('Datei konnte nicht gelesen werden');
      setImportFile(null);
    }
  };

  // Labels und Reihenfolge-Stufen kommen ausschliesslich aus dem Backend, damit
  // neue Entitaeten hier nicht nachgepflegt werden muessen.
  const entityMeta = React.useMemo(() => {
    const map = new Map<string, { label: string; order: number }>();
    exportableEntities.forEach((e) => map.set(e.key, { label: e.label, order: e.order }));
    return map;
  }, [exportableEntities]);

  const importEntityGroups = React.useMemo(() => {
    const enriched = importFileEntities.map((e) => {
      const meta = entityMeta.get(e.key);
      return {
        ...e,
        label: meta?.label ?? e.key.replace(/_/g, ' '),
        order: meta?.order ?? UNKNOWN_ENTITY_ORDER,
      };
    });
    const orders = Array.from(new Set(enriched.map((e) => e.order))).sort((a, b) => a - b);
    return orders.map((order) => ({
      order,
      label: IMPORT_CATEGORY_LABELS[order] ?? IMPORT_CATEGORY_LABELS[UNKNOWN_ENTITY_ORDER],
      entities: enriched
        .filter((e) => e.order === order)
        .sort((a, b) => a.label.localeCompare(b.label, 'de')),
    }));
  }, [importFileEntities, entityMeta]);

  const toggleImportEntity = (key: string) => {
    setSelectedImportEntities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllImportInCategory = (keys: string[]) => {
    setSelectedImportEntities((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const deselectAllImportInCategory = (keys: string[]) => {
    setSelectedImportEntities((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.delete(k));
      return next;
    });
  };

  const selectAllImportEntities = () => {
    setSelectedImportEntities(new Set(importFileEntities.map((e) => e.key)));
  };

  const deselectAllImportEntities = () => {
    setSelectedImportEntities(new Set());
  };

  const handleImport = async () => {
    if (!importFile) {
      showError('Bitte eine JSON-Datei auswählen');
      return;
    }
    if (selectedImportEntities.size === 0) {
      showError('Bitte mindestens eine Entität zum Import auswählen');
      return;
    }
    try {
      setImportLoading(true);
      setImportResult(null);
      const result = await importExportService.importData(importFile, {
        entities: Array.from(selectedImportEntities),
        strategy: importStrategy,
      });
      setImportResult(result);
      const totalImported = Object.values(result.imported).reduce((sum, arr) => sum + arr.length, 0);
      if (result.errors.length > 0) {
        showError(`Import mit Fehlern: ${totalImported} importiert, ${result.errors.length} Fehler`);
      } else {
        showSuccess(`Import erfolgreich: ${totalImported} Einträge importiert`);
      }
    } catch {
      // Error displayed by axios interceptor
    } finally {
      setImportLoading(false);
    }
  };

  const toggleExportEntity = (key: string) => {
    setSelectedExportEntities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllExportEntities = () => {
    setSelectedExportEntities(new Set(exportableEntities.map((e) => e.key)));
  };

  const deselectAllExportEntities = () => {
    setSelectedExportEntities(new Set());
  };

  return (
    <Box>
      <PageHeader
        title="Datenimport/-export"
        subtitle="Daten exportieren und importieren"
        breadcrumbs={[{ label: 'Datenimport/-export' }]}
      />

      <Card>
        <CardContent>
          {(canExport || canImport) && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Exportieren Sie Daten als JSON- oder ZIP-Datei oder importieren Sie zuvor exportierte Daten.
              Beim Import werden .json- und .zip-Dateien akzeptiert.
            </Alert>
          )}

          {canExport && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Export
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Wählen Sie die zu exportierenden Entitäten aus:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button size="small" variant="outlined" onClick={selectAllExportEntities}>
                  Alle auswählen
                </Button>
                <Button size="small" variant="outlined" onClick={deselectAllExportEntities}>
                  Alle abwählen
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 1,
                  mb: 2,
                }}
              >
                {exportableEntities.map((e) => (
                  <Box key={e.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id={`export-${e.key}`}
                      checked={selectedExportEntities.has(e.key)}
                      onChange={() => toggleExportEntity(e.key)}
                    />
                    <Typography
                      component="label"
                      htmlFor={`export-${e.key}`}
                      variant="body2"
                      sx={{ cursor: 'pointer' }}
                    >
                      {e.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exportIncludeDependencies}
                    onChange={(e) => setExportIncludeDependencies(e.target.checked)}
                    color="primary"
                  />
                }
                label="Abhängigkeiten mit exportieren (Kunden, Audits etc. für Projekte/Dateien)"
              />
              {!exportIncludeDependencies && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Ohne Abhängigkeiten kann der Import fehlschlagen (z.B. kunde_id nicht gemappt).
                  Nur für Experten: Export von Einzelelementen ohne Verknüpfungen.
                </Alert>
              )}
              <Button
                variant="contained"
                startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exportLoading || selectedExportEntities.size === 0}
              >
                {exportLoading ? 'Exportiere...' : 'Export starten'}
              </Button>
            </Box>
          )}

          {canImport && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Import
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Laden Sie eine zuvor exportierte JSON- oder ZIP-Datei hoch:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  type="file"
                  inputProps={{ accept: '.json,.zip' }}
                  onChange={(e) => {
                    const f = (e.target as HTMLInputElement).files?.[0];
                    handleImportFileChange(f || null);
                  }}
                  InputProps={{
                    inputProps: { style: { padding: 8 } },
                  }}
                />

                {importFileEntities.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Zu importierende Entitäten auswählen:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button size="small" variant="outlined" onClick={selectAllImportEntities}>
                        Alle auswählen
                      </Button>
                      <Button size="small" variant="outlined" onClick={deselectAllImportEntities}>
                        Alle abwählen
                      </Button>
                    </Box>
                    {importEntityGroups.map((group) => {
                      const entities = group.entities;
                      const groupKeys = entities.map((e) => e.key);
                      return (
                        <Paper key={group.order} variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {group.label}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button size="small" onClick={() => selectAllImportInCategory(groupKeys)}>
                                Alle
                              </Button>
                              <Button size="small" onClick={() => deselectAllImportInCategory(groupKeys)}>
                                Keine
                              </Button>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                              gap: 1,
                            }}
                          >
                            {entities.map((e) => (
                              <Box key={e.key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                  type="checkbox"
                                  id={`import-${e.key}`}
                                  checked={selectedImportEntities.has(e.key)}
                                  onChange={() => toggleImportEntity(e.key)}
                                />
                                <Typography
                                  component="label"
                                  htmlFor={`import-${e.key}`}
                                  variant="body2"
                                  sx={{ cursor: 'pointer', flex: 1 }}
                                >
                                  {e.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({e.count})
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}

                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <FormLabel sx={{ mb: 0.5 }}>Strategie bei bestehenden Einträgen (alle Kategorien)</FormLabel>
                  <Select
                    value={importStrategy}
                    onChange={(e) => setImportStrategy(e.target.value as typeof importStrategy)}
                  >
                    <MenuItem value="skip_existing">Bestehende überspringen</MenuItem>
                    <MenuItem value="overwrite">Bestehende überschreiben</MenuItem>
                    <MenuItem value="fail_on_conflict">Bei Konflikt Fehler</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={importLoading ? <CircularProgress size={20} /> : <UploadIcon />}
                  onClick={handleImport}
                  disabled={importLoading || !importFile || selectedImportEntities.size === 0}
                >
                  {importLoading ? 'Importiere...' : 'Import starten'}
                </Button>
              </Box>

              {importResult && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Import-Ergebnis
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                    {Object.entries(importResult.imported).map(([entity, items]) =>
                      items.length > 0 ? (
                        <Typography key={entity} variant="body2" sx={{ mb: 0.5 }}>
                          {entity}: {items.length} importiert
                        </Typography>
                      ) : null
                    )}
                    {Object.entries(importResult.skipped).map(([entity, items]) =>
                      items.length > 0 ? (
                        <Typography key={`skip-${entity}`} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {entity}: {items.length} übersprungen
                        </Typography>
                      ) : null
                    )}
                    {importResult.errors.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="error" gutterBottom>
                          Fehler:
                        </Typography>
                        {importResult.errors.map((err, idx) => (
                          <Typography key={idx} variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {err}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DatenImportExportPage;
